import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { GeoJSON } from 'ol/format';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { defaults as defaultControls } from 'ol/control';
import axios from 'axios';

// Currency formatting function
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return 'N/A';
  
  // Try to convert string to number, handle commas and other separators
  const number = typeof amount === 'number' ? amount : parseFloat(amount.toString().replace(/[^0-9.-]+/g, ''));
  
  // Check if conversion was successful
  if (isNaN(number)) return 'N/A';
  
  return new Intl.NumberFormat('en-ZM', { 
    style: 'decimal', 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(number);
};

// Register UTM 35S projection (your coordinate system)
proj4.defs('EPSG:25835', '+proj=utm +zone=35 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
register(proj4);

const PlotMap = () => {
  const mapInstance = useRef(null);
  const [plots, setPlots] = useState(null);
  const [plotsMikango, setPlotsMikango] = useState([]);
  const [mergedPlots, setMergedPlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [matchStats, setMatchStats] = useState({ total: 0, matched: 0, unmatched: 0 });

  const server_app_api_php = window.SERVER_APP_API_PHP;

  // Status colors
  const statusColors = {
    sold: [156, 39, 176, 0.7],  // Purple
    unsold: [76, 175, 80, 0.7],     // Green
    default: [121, 85, 72, 0.7]   // Brown for unmatched
  };

  // Helper function to normalize plot status from MariaDB
  const normalizePlotStatus = (status) => {
    if (!status || typeof status !== 'string') return 'unsold';
    
    const normalized = status.trim().toLowerCase();
    return normalized === 'sold' ? 'sold' : 'unsold';
  };

  // Helper function to extract plot number from MariaDB format (like "EA71")
  const extractPlotNumberFromMikango = (plotString) => {
    if (!plotString) return null;
    
    const str = plotString.toString().trim();
    
    // Handle formats like "EA71", "EA-71", "EA 71", etc.
    // Extract the number part after "EA"
    const match = str.match(/EA\s*[-_]?\s*(\d+)/i);
    if (match) {
      return match[1]; // Returns "71" from "EA71"
    }
    
    // If no "EA" prefix, try to extract any number
    const numberMatch = str.match(/\d+/);
    return numberMatch ? numberMatch[0] : null;
  };

  // Helper function to extract plot number from PostgreSQL format (like "F/3208/3/E/71")
  const extractPlotNumberFromPG = (plotString) => {
    if (!plotString) return null;
    
    const str = plotString.toString().trim();
    
    // Extract the last number after the last slash
    // For format "F/3208/3/E/71" -> returns "71"
    const parts = str.split('/');
    const lastPart = parts[parts.length - 1];
    
    // Check if last part contains a number
    const numberMatch = lastPart.match(/\d+/);
    if (numberMatch) {
      return numberMatch[0];
    }
    
    // If no number in last part, try to find any number in the string
    const anyNumberMatch = str.match(/\d+/);
    return anyNumberMatch ? anyNumberMatch[0] : null;
  };

  // Fetch plots from MariaDB (plot details)
  useEffect(() => {
    const fetchDataMikango = async () => {
      const formData = new FormData();
      formData.append('apiType', 'viewPlotsMikango');
      try {
        const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setPlotsMikango(response.data);
        console.log("Data from MariaDB (Mikango):", response.data);
        
        // Log some sample data for debugging
        if (response.data && response.data.length > 0) {
          console.log("Sample MariaDB entries (first 5):");
          response.data.slice(0, 5).forEach((plot, index) => {
            console.log(`${index + 1}. plotNumbers: "${plot.plotNumbers}", plotStatus: "${plot.plotStatus}", normalized: "${normalizePlotStatus(plot.plotStatus)}"`);
          });
        }
      } catch (error) {
        console.error('Error fetching Mikango plots:', error);
      }
    };
    fetchDataMikango();
  }, [server_app_api_php]);

  // Fetch plots from PostgreSQL (map data - only geometry and property_n)
  useEffect(() => {
    axios.get('https://pythonridgehillmaps.kanchtech.com/api/plots')
      .then(response => {
        console.log('✅ Data loaded from PostgreSQL:', response.data.features.length, 'plots');
        setPlots(response.data);
        
        // Log some sample data for debugging
        if (response.data && response.data.features.length > 0) {
          console.log("Sample PostgreSQL entries (first 5):");
          response.data.features.slice(0, 5).forEach((feature, index) => {
            console.log(`${index + 1}. property_n: "${feature.properties.property_n}"`);
          });
        }
      })
      .catch(error => {
        console.error('❌ Error fetching plots:', error);
        setLoading(false);
      });
  }, []);

  // Merge data from both sources when both are available
  useEffect(() => {
    if (plots && plotsMikango.length > 0) {
      console.log('🔗 Merging data from MariaDB and PostgreSQL...');
      
      // Create a lookup map from MariaDB data
      const mikangoLookup = {};
      let mikangoCount = 0;
      
      plotsMikango.forEach(plot => {
        const plotNo = extractPlotNumberFromMikango(plot.plotNumbers);
        if (plotNo !== null) {
          // Normalize the plot status
          const normalizedStatus = normalizePlotStatus(plot.plotStatus);
          
          // Store all relevant data from MariaDB
          mikangoLookup[plotNo] = {
            plotStatus: normalizedStatus,
            plotAreas: plot.plotAreas || null,
            plotNumbers: plot.plotNumbers,
            plotPrices: plot.plotPrices || null, // Store price for formatting
            originalPlotStatus: plot.plotStatus || '', // Keep original for display
            // Include any other relevant fields from MariaDB
            ...plot
          };
          mikangoCount++;
        }
      });

      console.log(`📊 Created lookup map from MariaDB: ${mikangoCount} entries with valid plot numbers`);
      console.log('Sample MariaDB lookup entries (first 5):', Object.entries(mikangoLookup).slice(0, 5));

      // Clone the plots GeoJSON to avoid mutating the original
      const mergedFeatures = JSON.parse(JSON.stringify(plots.features));
      let matchedCount = 0;
      let unmatchedCount = 0;
      let soldCount = 0;
      let unsoldCount = 0;

      // Update each feature with data from MariaDB if found
      mergedFeatures.forEach(feature => {
        const pgPlotNo = extractPlotNumberFromPG(feature.properties.property_n);
        feature.properties.original_property_n = feature.properties.property_n;
        feature.properties.extracted_plot_no = pgPlotNo;
        
        if (pgPlotNo !== null && mikangoLookup[pgPlotNo]) {
          // Found a match!
          const mikangoData = mikangoLookup[pgPlotNo];
          
          // Track status for statistics
          if (mikangoData.plotStatus === 'sold') {
            soldCount++;
          } else {
            unsoldCount++;
          }
          
          // Replace properties with MariaDB data
          feature.properties = {
            property_n: feature.properties.property_n, // Keep original property_n from PG
            plotStatus: mikangoData.plotStatus,
            plotAreas: mikangoData.plotAreas || null,
            plotNumbers: mikangoData.plotNumbers,
            plotPrices: mikangoData.plotPrices || null,
            originalPlotStatus: mikangoData.originalPlotStatus,
            area: mikangoData.plotAreas || null, // For compatibility with existing code
            matched: true,
            mikango_data: mikangoData
          };
          
          matchedCount++;
          
          console.log(`✅ Matched: PostgreSQL "${feature.properties.property_n}" -> MariaDB "${mikangoData.plotNumbers}" (Status: ${mikangoData.plotStatus}, Original: "${mikangoData.originalPlotStatus}")`);
        } else {
          // No match found - default to unsold
          feature.properties = {
            property_n: feature.properties.property_n,
            plotStatus: 'unsold',
            plotAreas: null,
            plotNumbers: null,
            plotPrices: null,
            originalPlotStatus: '',
            area: null,
            matched: false
          };
          
          unsoldCount++;
          unmatchedCount++;
          
          if (pgPlotNo) {
            console.log(`❌ No match for: PostgreSQL "${feature.properties.property_n}" (extracted: ${pgPlotNo})`);
          } else {
            console.log(`❌ Could not extract plot number from: "${feature.properties.property_n}"`);
          }
        }
      });

      // Update match statistics
      setMatchStats({
        total: mergedFeatures.length,
        matched: matchedCount,
        unmatched: unmatchedCount,
        sold: soldCount,
        unsold: unsoldCount
      });

      // Create the merged GeoJSON
      const mergedGeoJSON = {
        type: 'FeatureCollection',
        features: mergedFeatures
      };

      setMergedPlots(mergedGeoJSON);
      setLoading(false);
      
      console.log(`📈 Merge complete: ${matchedCount} matched (${soldCount} sold, ${unsoldCount} unsold), ${unmatchedCount} unmatched out of ${mergedFeatures.length} total`);
      console.log('Sample merged features (first 3):', mergedFeatures.slice(0, 3));
    }
  }, [plots, plotsMikango]);

  // Callback ref for the map container
  const mapContainerRef = useCallback((node) => {
    if (node === null) {
      // The node is null, meaning the container is unmounting
      if (mapInstance.current) {
        mapInstance.current.setTarget(null);
        mapInstance.current = null;
        setMapReady(false);
      }
    } else {
      // The node is available, initialize the map
      console.log('🗺️ Initializing map on node:', node);
      mapInstance.current = new Map({
        target: node,
        controls: defaultControls(),
        layers: [
          new TileLayer({
            source: new OSM()
          })
        ],
        view: new View({
          center: fromLonLat([28.3228, -15.3875]), // Lusaka, Zambia
          zoom: 14,
          minZoom: 10,
          maxZoom: 20
        })
      });
      setMapReady(true);
      console.log('✅ Map initialized');
    }
  }, []);

  // Add merged plots to map when data is ready and map is ready
  useEffect(() => {
    if (!mapInstance.current || !mergedPlots || !mapReady) {
      console.log('⏳ Waiting for map/data:', {
        map: !!mapInstance.current,
        mergedPlots: !!mergedPlots,
        mapReady,
        features: mergedPlots?.features?.length
      });
      return;
    }

    console.log('🎨 Adding merged plots to map...');

    try {
      // Create style function
      const styleFunction = (feature) => {
        const plotStatus = feature.get('plotStatus'); // This should be 'sold' or 'unsold'
        const isMatched = feature.get('matched');
        
        console.log(`Styling feature: property_n=${feature.get('property_n')}, plotStatus=${plotStatus}, matched=${isMatched}`);
        
        // Determine color based on plotStatus
        let color;
        if (isMatched) {
          // Use sold or unsold color based on plotStatus
          if (plotStatus?.toLowerCase() === 'sold') {
            color = statusColors.sold;
          } else {
            color = statusColors.unsold;
          }
        } else {
          // Unmatched plots get the default color
          color = statusColors.default;
        }
        
        return new Style({
          fill: new Fill({
            color: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
          }),
          stroke: new Stroke({
            color: isMatched ? [0, 0, 0, 1] : [255, 0, 0, 0.5], // Red border for unmatched
            width: isMatched ? 2 : 1,
            lineDash: isMatched ? [] : [5, 5] // Dashed line for unmatched
          }),
          text: new Text({
            text: feature.get('property_n')?.toString() || '',
            font: 'bold 12px Arial',
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
            offsetY: -15
          })
        });
      };

      // Create GeoJSON reader with projection definitions
      const geoJSONReader = new GeoJSON({
        dataProjection: 'EPSG:25835',  // Your UTM 35S data
        featureProjection: 'EPSG:3857' // Web Mercator for display
      });

      // Read features from merged GeoJSON
      const vectorFeatures = geoJSONReader.readFeatures(mergedPlots);
      console.log(`📊 Converted ${vectorFeatures.length} features`);
      
      // Log the first few features to verify plotStatus
      vectorFeatures.slice(0, 5).forEach((feature, index) => {
        console.log(`Feature ${index + 1}: property_n=${feature.get('property_n')}, plotStatus=${feature.get('plotStatus')}, matched=${feature.get('matched')}`);
      });

      // Create vector source and layer
      const vectorSource = new VectorSource({
        features: vectorFeatures
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: styleFunction
      });

      // Clear existing vector layers
      const existingLayers = mapInstance.current.getLayers().getArray();
      existingLayers.forEach(layer => {
        if (layer instanceof VectorLayer) {
          mapInstance.current.removeLayer(layer);
        }
      });

      // Add new layer to map
      mapInstance.current.addLayer(vectorLayer);
      console.log('✅ Merged plots added to map');

      // Fit view to show all plots
      const extent = vectorSource.getExtent();
      if (extent && extent[0] !== Infinity) {
        mapInstance.current.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          maxZoom: 18
        });
        console.log('📍 Map view fitted to plots');
      }

      // Add click interaction
      mapInstance.current.on('click', (event) => {
        const feature = mapInstance.current.forEachFeatureAtPixel(
          event.pixel,
          (feature) => feature
        );
        
        if (feature) {
          const props = feature.getProperties();
          const isMatched = props.matched;
          const plotStatus = props.plotStatus || 'unsold';
          const originalPlotStatus = props.originalPlotStatus || '';
          
          // Format the price and area values
          const formattedPrice = props.plotPrices ? `K${formatCurrency(props.plotPrices)}` : 'N/A';
          const formattedArea = props.plotAreas || props.area ? `${formatCurrency(props.plotAreas || props.area)} m²` : 'N/A';
          
          let popupContent = `
            <div style="padding: 10px; max-width: 300px;">
              <div style="display: flex; align-items: center; margin-bottom: 10px;">
                <div style="width: 12px; height: 12px; background-color: ${plotStatus === 'sold' ? '#4CAF50' : isMatched ? '#9C27B0' : '#795548'}; 
                      margin-right: 8px; border-radius: 50%;"></div>
                <h3 style="margin: 0; color: ${isMatched ? '#333' : '#d32f2f'};">Plot Details</h3>
              </div>
              <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                <tr>
                  <td style="padding: 4px 0; font-weight: bold; width: 120px;">Plot No:</td>
                  <td style="padding: 4px 0;">${props.property_n || 'N/A'}</td>
                </tr>
          `;
          
          if (isMatched) {
            popupContent += `
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">System Plot No:</td>
                  <td style="padding: 4px 0;">${props.plotNumbers || 'N/A'}</td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Status:</td>
                  <td style="padding: 4px 0; color: ${plotStatus === 'sold' ? '#4CAF50' : '#9C27B0'}">
                    ${plotStatus} 
                  </td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Area:</td>
                  <td style="padding: 4px 0;">${formattedArea}</td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Price:</td>
                  <td style="padding: 4px 0;">${formattedPrice}</td>
                </tr>
            `;
          } else {
            popupContent += `
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Status:</td>
                  <td style="padding: 4px 0; color: #795548">unsold (Not matched)</td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Area:</td>
                  <td style="padding: 4px 0;">${formattedArea}</td>
                </tr>
                <tr>
                  <td style="padding: 4px 0; font-weight: bold;">Price:</td>
                  <td style="padding: 4px 0;">${formattedPrice}</td>
                </tr>
            `;
          }
          
          popupContent += `</table>`;
          
          // Create and show popup
          const popup = document.createElement('div');
          popup.innerHTML = popupContent;
          popup.style.cssText = `
            position: absolute;
            background: white;
            padding: 10px;
            border-radius: 5px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.3);
            left: ${event.pixel[0] + 10}px;
            top: ${event.pixel[1] - 10}px;
            z-index: 1000;
            max-width: 300px;
            max-height: 400px;
            overflow-y: auto;
            border-left: 4px solid ${isMatched ? '#4CAF50' : '#d32f2f'};
          `;
          
          // Remove existing popups
          document.querySelectorAll('.ol-popup').forEach(p => p.remove());
          popup.className = 'ol-popup';
          mapInstance.current.getTargetElement().appendChild(popup);
          
          // Close popup on map click
          const closePopup = () => {
            popup.remove();
            mapInstance.current.un('click', closePopup);
          };
          setTimeout(() => {
            mapInstance.current.once('click', closePopup);
          }, 100);
        }
      });

    } catch (error) {
      console.error('❌ Error adding merged plots to map:', error);
    }
  }, [mergedPlots, mapReady, statusColors]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        background: '#f5f5f5'
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '20px',
          color: '#333'
        }}>
          Loading Real Estate Map...
        </div>
        <div style={{
          width: '200px',
          height: '4px',
          background: '#e0e0e0',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '60%',
            height: '100%',
            background: 'linear-gradient(90deg, #667eea, #764ba2)',
            animation: 'loading 2s infinite'
          }}></div>
        </div>
        <div style={{
          marginTop: '20px',
          fontSize: '14px',
          color: '#666'
        }}>
          Fetching data from MariaDB and PostgreSQL...
        </div>
        <style>{`
          @keyframes loading {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <div 
        ref={mapContainerRef}
        style={{ 
          width: '100%', 
          height: '100%',
          background: '#f0f0f0'
        }}
      />
      
      {/* Legend */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        minWidth: '70px'
      }}>
        <h5 style={{ marginTop: 0, marginBottom: '5px', color: '#333' }}>
          Key
        </h5>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '3px' 
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            backgroundColor: `rgba(${statusColors.sold[0]}, ${statusColors.sold[1]}, ${statusColors.sold[2]}, ${statusColors.sold[3]})`,
            marginRight: '5px',
            border: '2px solid #000',
            borderRadius: '3px'
          }}></div>
          <span style={{ fontWeight: '200' }}>Sold</span>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '8px' 
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            backgroundColor: `rgba(${statusColors.unsold[0]}, ${statusColors.unsold[1]}, ${statusColors.unsold[2]}, ${statusColors.unsold[3]})`,
            marginRight: '5px',
            border: '2px solid #000',
            borderRadius: '3px'
          }}></div>
          <span style={{ fontWeight: '200' }}>Unsold</span>
        </div>
        
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '8px' 
          }}>
            <div style={{
              width: '10px',
              height: '10px',
              backgroundColor: `rgba(${statusColors.default[0]}, ${statusColors.default[1]}, ${statusColors.default[2]}, ${statusColors.default[3]})`,
              marginRight: '5px',
              border: '1px dashed #f00',
              borderRadius: '3px'
            }}></div>
            <span style={{ fontWeight: '200' }}>N/A</span>
          </div>
        </div>
        
      </div>

     
     {/* Coordinates info */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '10px 15px',
        borderRadius: '5px',
        fontSize: '12px',
        color: '#666',
        zIndex: 1000
      }}>
        <strong>Click on any plot to see details.</strong><br />
        Red dashed border indicates unmatched plots.
      </div>
    </div>
  );
};

export default PlotMap;